// store/bbps/pg/pgSaga.ts
import { call, put, takeLatest } from "redux-saga/effects";
import type { PayloadAction } from "@reduxjs/toolkit";

import {
  RazorpayStartRequested,
  RazorpayOrderSucceeded,
  RazorpayPaymentOpening,
  RazorpayPaymentSucceeded,
  RazorpayPaymentFailed,
  RazorpayPaymentCancelled,
  RazorpayOrder,
} from "./razorpaySlice";

import { billPaymentRequested } from "store/bbps/billPayment/billPaymentSlice";
import type { BillPaymentPayload } from "features/bbps/biller/billerFlowTypes";
import type { NormalizedError } from "services/axios.services/interceptors/commonInterceptors";
import { currentGateway } from "features/paymentGateway/currentPaymentGateway";
import { StartPaymentArgs } from "hooks/Bbps/billPayment/useBillPayment";
import {
  CreateRazorpayOrderPayload,
  createRazorpayOrder,
} from "services/api.services/payments/razorpay/razorpayPaymentService";

function* handleRazorpayStartRequested(
  action: PayloadAction<StartPaymentArgs>,
) {
  const args = action.payload;

  if (!args.amountPaise || isNaN(args.amountPaise)) {
    const err: NormalizedError = {
      status: 400,
      message: "Invalid payment amount",
      code: "INVALID_AMOUNT",
      details: null,
    };
    yield put(RazorpayPaymentFailed(err));
    return;
  }

  try {
    // 1) Razorpay order create
    const orderPayload: CreateRazorpayOrderPayload = {
      amount: args.amountPaise,
      currency: "INR",
      refId: args.refId,
      billerId: args.billerId,
    };

    // console.log({orderPayload});

    const order: RazorpayOrder = yield call(createRazorpayOrder, orderPayload);
    // console.log({order});

    yield put(RazorpayOrderSucceeded(order));

    // 2) Gateway open
    yield put(RazorpayPaymentOpening());

    // const RazorpayResult: PaymentGatewayResult = yield call(

    const RazorpayResult: { data: any } = yield call(
      [currentGateway, currentGateway.openPayment],
      {
        amountPaise: order.amount,
        currency: order.currency,
        orderId: order.id,
        customerName: args.customerDetails["REMITTER NAME"],
        customerMobile: args.customerMobile,
        notes: {
          bbpsRefId: args.refId,
          billerId: args.billerId,
        },
      },
    );

    // console.log({RazorpayResult});

    if (RazorpayResult.data.status === "cancelled") {
      yield put(RazorpayPaymentCancelled());
      return;
    }

    if (
      RazorpayResult.data.status === "failed" ||
      !RazorpayResult.data.success
    ) {
      const err: NormalizedError = {
        status: 400,
        message: RazorpayResult.data.message || "Payment failed",
        code: "Razorpay_FAILED",
        details: RazorpayResult,
      };
      yield put(RazorpayPaymentFailed(err));
      return;
    }

    // 3) Razorpay success → update Razorpay slice
    const successPayload = {
      paymentId: RazorpayResult.data.success.paymentId,
      orderId: RazorpayResult.data.success.orderId,
    };

    yield put(RazorpayPaymentSucceeded(successPayload));

    // 4) Razorpay success → BBPS billPayment API call
    const billPaymentPayload: BillPaymentPayload = {
      refId: args.refId,
      billerId: args.billerId,
      customerParams: args.customerParams,
      customerDetails: args.customerDetails,
      deviceDetails: {
        MAC: "04-D9-C8-64-5E-3F",
        IP: "122.160.88.102",
      },
      customerMobile: args.customerMobile,
      isQuickPay: "No",
      amount: { amount: String(args.amountPaise) },
      paymentInformation: {
        VPA: args.customerDetails["VPA"], // optional
      },
      txn: {
        ts: new Date().toISOString(),
        paymentRefId: RazorpayResult.data.success.paymentId,
      },
      paymentMethod: {
        splitPay: "No",
        OFFUSPay: "No",
        paymentMode: args.paymentMode || "UPI",
      },
    };

    yield put(billPaymentRequested(billPaymentPayload));
  } catch (e: any) {
    const err: NormalizedError = {
      status: 500,
      message: e?.message || "Payment gateway error",
      code: "Razorpay_EXCEPTION",
      details: e,
    };
    yield put(RazorpayPaymentFailed(err));
  }
}

export function* razorpaySaga() {
  yield takeLatest(RazorpayStartRequested.type, handleRazorpayStartRequested);
}
